import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, AlertTriangle, ExternalLink, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/App';
import usePushNotifications from '@/hooks/usePushNotifications';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.expatssphere.twa';

/**
 * Detects the "stuck push" state and offers a guided recovery.
 *
 * Trigger criteria (all must be true):
 *   - User is logged in
 *   - Browser supports Notifications
 *   - Notification.permission === 'denied'
 *   - User has not intentionally opted out (push_manually_disabled flag)
 *   - User has not dismissed the banner this session
 *
 * Fast-paths:
 *   - "Try Now" calls Notification.requestPermission() — if the user has
 *     already reset the site permission via Settings, the OS prompt fires
 *     instantly and they're done in one tap.
 *   - On document visibility change, re-check permission state. If it
 *     flipped denied → default while they were in Settings, auto-call
 *     requestPermission() so they don't even need to find the banner again.
 *
 * Recovery is environment-aware: TWA (Play Store install) gets a one-tap
 * reinstall deep-link; PWA / browser / iOS each get the correct path.
 */
const PushRecoveryBanner = () => {
  const { user } = useAuth();
  const { subscribe } = usePushNotifications();
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tryingNow, setTryingNow] = useState(false);
  // Track current Notification.permission in state so the banner reacts to
  // visibility changes without remounting.
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  // Avoid auto-prompting more than once per session
  const autoPromptedRef = useRef(false);

  const env = (() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return { kind: 'browser', label: 'browser' };
    }
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    const isTWA = typeof document !== 'undefined'
      && document.referrer.startsWith('android-app://');
    if (isTWA) return { kind: 'twa', label: 'Google Play app' };
    if (isStandalone && isIOS) return { kind: 'ios-pwa', label: 'iOS app' };
    if (isStandalone) return { kind: 'android-pwa', label: 'installed app' };
    if (isIOS) return { kind: 'ios-safari', label: 'Safari' };
    return { kind: 'browser', label: 'Chrome' };
  })();

  // Try the simplest fix first — re-request permission. Works instantly if the
  // user has already reset the site permission and now it's 'default'.
  const tryNow = useCallback(async () => {
    if (tryingNow) return;
    setTryingNow(true);
    try {
      if (typeof Notification === 'undefined') return;
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        // Actually subscribe to push and sync to backend
        const ok = await subscribe();
        if (ok) {
          toast.success('Push notifications enabled!');
          setShow(false);
          setShowModal(false);
        } else {
          toast.error('Permission granted but subscription failed. Please try again.');
        }
      } else if (result === 'denied') {
        toast.error('Notifications are still blocked — please follow the steps to reset site permissions.', {
          duration: 7000
        });
        setShowModal(true);
      } else {
        // 'default' — user dismissed the prompt; do nothing
      }
    } catch (e) {
      console.error('tryNow failed', e);
    } finally {
      setTryingNow(false);
    }
  }, [subscribe, tryingNow]);

  // Initial visibility — show the banner if the trigger conditions match
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'denied') return;

    let manuallyDisabled = false;
    try {
      manuallyDisabled = localStorage.getItem('push_manually_disabled') === '1'
        || localStorage.getItem('push_manually_disabled') === 'true';
    } catch {}
    if (manuallyDisabled) return;

    let dismissed = false;
    try { dismissed = sessionStorage.getItem('pushRecoveryDismissed') === '1'; } catch {}
    if (dismissed) return;

    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, [user]);

  // Auto-recovery: when the user returns to the app from Settings, check if
  // they just unblocked us. If permission flipped denied → default, fire the
  // browser prompt automatically — they get a one-tap finish without
  // hunting for our banner again.
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === 'undefined') return;

    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const current = Notification.permission;
      setPermission(current);

      if (current === 'granted') {
        // Already granted via some other path — dismiss the banner
        setShow(false);
        setShowModal(false);
        return;
      }
      if (current === 'default' && !autoPromptedRef.current) {
        autoPromptedRef.current = true;
        // Brief delay so the OS UI has settled after backgrounding
        setTimeout(() => { tryNow(); }, 400);
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user, tryNow]);

  const dismiss = () => {
    try { sessionStorage.setItem('pushRecoveryDismissed', '1'); } catch {}
    setShow(false);
  };

  const neverShow = () => {
    try { localStorage.setItem('push_manually_disabled', 'true'); } catch {}
    setShow(false);
    setShowModal(false);
  };

  // Last-resort path: open the Play Store page so the user can uninstall
  // and reinstall, which fully flushes TWA cache and push subscription state.
  const openPlayStore = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const steps = (() => {
    switch (env.kind) {
      case 'twa':
      case 'android-pwa':
      case 'browser':
        return [
          'Open Chrome (the browser app)',
          'Tap ⋮ menu → Settings → Site settings → All sites',
          'Find and tap https://expatssphere.com',
          'Tap "Delete data & reset permissions" (blue button)',
          'Confirm — this logs you out but keeps your account intact',
          'Reopen ExpatsSphere and log back in',
          'When the notifications prompt appears, tap Allow'
        ];
      case 'ios-pwa':
      case 'ios-safari':
        return [
          'Open the iPhone Settings app',
          'Scroll to Safari → Advanced → Website Data',
          'Find expatssphere.com → swipe left → Delete',
          'Reopen ExpatsSphere and log back in',
          'When the notifications prompt appears, tap Allow'
        ];
      default:
        return [];
    }
  })();

  if (!show) return null;
  if (permission === 'granted') return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          className="fixed bottom-20 left-3 right-3 md:left-auto md:right-4 md:bottom-4 md:w-[380px] z-40"
          data-testid="push-recovery-banner"
        >
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-amber-300 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-bold">Push notifications are stuck</span>
              </div>
              <button
                onClick={dismiss}
                className="p-1 hover:bg-white/20 rounded-full"
                data-testid="push-recovery-dismiss"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <p className="text-sm text-gray-700 leading-snug">
                Your browser auto-blocked notifications for ExpatsSphere. Tap below to try the fastest fix first.
              </p>
              <Button
                size="sm"
                onClick={tryNow}
                disabled={tryingNow}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full font-semibold"
                data-testid="push-recovery-try-now-btn"
              >
                <RotateCcw className={`w-4 h-4 mr-1.5 ${tryingNow ? 'animate-spin' : ''}`} />
                {tryingNow ? 'Trying…' : 'Try now'}
              </Button>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="w-full text-xs text-amber-700 hover:text-amber-800 underline"
                data-testid="push-recovery-fix-btn"
              >
                Didn't work? Show me how to reset
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Modal with step-by-step recovery */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-4"
            onClick={() => setShowModal(false)}
            data-testid="push-recovery-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold">Reset push notifications</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Detected: {env.label}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-muted rounded-full"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                  <strong>What happened:</strong> after several push delivery
                  failures, Chrome automatically revoked notification permission
                  for this site. The only fix is to reset the site&apos;s stored
                  permissions — your account and all data stay intact.
                </div>
                <ol className="space-y-2.5 text-sm">
                  {steps.map((s, i) => (
                    <li key={i} className="flex gap-3" data-testid={`push-recovery-step-${i + 1}`}>
                      <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-gray-700 leading-snug">{s}</span>
                    </li>
                  ))}
                </ol>

                {/* Last-resort reinstall option — TWA users only */}
                {env.kind === 'twa' && (
                  <div className="pt-2 border-t border-dashed border-amber-200 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      <strong>Still stuck after the steps above?</strong> The fastest
                      bulletproof fix is to reinstall — takes about 60 seconds.
                    </p>
                    <Button
                      variant="outline"
                      onClick={openPlayStore}
                      className="w-full rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="push-recovery-reinstall"
                    >
                      <ExternalLink className="w-4 h-4 mr-1.5" />
                      Reinstall from Play Store
                    </Button>
                  </div>
                )}

                <Button
                  onClick={() => setShowModal(false)}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="push-recovery-close"
                >
                  Got it
                </Button>
                <button
                  type="button"
                  onClick={neverShow}
                  className="block mx-auto text-xs text-muted-foreground hover:text-foreground underline"
                  data-testid="push-recovery-never-show"
                >
                  Don&apos;t show this again
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PushRecoveryBanner;
